import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, LogOut, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

export default function Account() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const activeUser = user || location.state?.user;

  useEffect(() => {
    if (!loading && !activeUser) {
      navigate('/', { replace: true });
    }
  }, [loading, activeUser, navigate]);

  if (loading || !activeUser) {
    return (
      <div data-testid="account-loading" className="min-h-screen flex items-center justify-center bg-white">
        <div className="h-10 w-10 border-4 border-emerald-700 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  return (
    <div data-testid="account-page" className="min-h-screen bg-emerald-50/50">
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.jpeg" alt="Four Pillars Global LLP" className="h-12 w-auto object-contain" />
            <div>
              <h1 className="text-lg font-bold text-gray-900 leading-tight">Eggfy</h1>
              <p className="text-xs text-emerald-700 font-medium">by Four Pillars Global</p>
            </div>
          </div>
          <Button data-testid="account-back-home-btn" variant="ghost" onClick={() => navigate('/')} className="text-gray-700">
            <ArrowLeft size={16} className="mr-2" /> Back to Home
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-16 max-w-xl">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
          {activeUser.picture ? (
            <img
              src={activeUser.picture}
              alt={activeUser.name}
              data-testid="account-avatar"
              referrerPolicy="no-referrer"
              className="h-24 w-24 rounded-full mx-auto mb-4 border-4 border-emerald-100 object-cover"
            />
          ) : (
            <div className="h-24 w-24 rounded-full mx-auto mb-4 bg-emerald-100 flex items-center justify-center text-3xl font-bold text-emerald-700">
              {activeUser.name?.charAt(0)}
            </div>
          )}
          <h2 data-testid="account-user-name" className="text-2xl font-bold text-gray-900">{activeUser.name}</h2>
          <p data-testid="account-user-email" className="text-gray-600 flex items-center justify-center gap-2 mt-2">
            <Mail size={16} className="text-emerald-700" /> {activeUser.email}
          </p>
          <p className="text-sm text-gray-500 mt-4">
            Welcome to your Eggfy account. You're signed in with Google.
          </p>
          <Button
            data-testid="account-logout-btn"
            onClick={handleLogout}
            variant="outline"
            className="mt-8 border-red-300 text-red-600 hover:bg-red-50"
          >
            <LogOut size={16} className="mr-2" /> Sign Out
          </Button>
        </div>
      </main>
    </div>
  );
}
